import React from 'react';
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'solid' | 'bordered' | 'light' | 'flat' | 'ghost';
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'default';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  isLoading?: boolean;
}
export function Button({
  children,
  variant = 'solid',
  color = 'primary',
  size = 'md',
  fullWidth = false,
  className = '',
  startContent,
  endContent,
  isLoading,
  disabled,
  ...props
}: ButtonProps) {
  const baseStyles = 'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl active:scale-95';
  const sizes = {
    sm: 'h-8 px-3 text-xs',
    md: 'h-10 px-4 py-2 text-sm',
    lg: 'h-12 px-6 text-base'
  };
  const colors = {
    primary: {
      solid: 'bg-seform-orange text-white hover:bg-orange-600 shadow-lg shadow-orange-500/30',
      bordered: 'border-2 border-seform-orange text-seform-orange hover:bg-orange-50',
      light: 'text-seform-orange hover:bg-orange-50',
      flat: 'bg-orange-100 text-seform-orange hover:bg-orange-200',
      ghost: 'border-2 border-seform-orange text-seform-orange hover:bg-seform-orange hover:text-white'
    },
    secondary: {
      solid: 'bg-seform-navy text-white hover:bg-indigo-900 shadow-lg shadow-indigo-900/30',
      bordered: 'border-2 border-seform-navy text-seform-navy hover:bg-indigo-50',
      light: 'text-seform-navy hover:bg-indigo-50',
      flat: 'bg-indigo-100 text-seform-navy hover:bg-indigo-200',
      ghost: 'border-2 border-seform-navy text-seform-navy hover:bg-seform-navy hover:text-white'
    },
    default: {
      solid: 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 shadow-sm',
      bordered: 'border-2 border-slate-300 text-slate-700 hover:bg-slate-50',
      light: 'text-slate-600 hover:bg-slate-100',
      flat: 'bg-slate-100 text-slate-700 hover:bg-slate-200',
      ghost: 'border-2 border-slate-300 text-slate-700 hover:bg-slate-700 hover:text-white'
    },
    danger: {
      solid: 'bg-red-500 text-white hover:bg-red-600 shadow-lg shadow-red-500/30',
      bordered: 'border-2 border-red-500 text-red-500 hover:bg-red-50',
      light: 'text-red-500 hover:bg-red-50',
      flat: 'bg-red-100 text-red-500 hover:bg-red-200',
      ghost: 'border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white'
    },
    success: {
      solid: 'bg-green-500 text-white hover:bg-green-600 shadow-lg shadow-green-500/30',
      bordered: 'border-2 border-green-500 text-green-500 hover:bg-green-50',
      light: 'text-green-500 hover:bg-green-50',
      flat: 'bg-green-100 text-green-500 hover:bg-green-200',
      ghost: 'border-2 border-green-500 text-green-500 hover:bg-green-500 hover:text-white'
    },
    warning: {
      solid: 'bg-yellow-500 text-white hover:bg-yellow-600 shadow-lg shadow-yellow-500/30',
      bordered: 'border-2 border-yellow-500 text-yellow-500 hover:bg-yellow-50',
      light: 'text-yellow-500 hover:bg-yellow-50',
      flat: 'bg-yellow-100 text-yellow-500 hover:bg-yellow-200',
      ghost: 'border-2 border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-white'
    }
  };
  // Safe fallback for color/variant combinations
  const colorStyle = colors[color]?.[variant] || colors.default.solid;
  return <button className={`
        ${baseStyles} 
        ${colorStyle} 
        ${sizes[size]} 
        ${fullWidth ? 'w-full' : ''} 
        ${className}
      `} disabled={disabled || isLoading} {...props} data-id="element-404">
      {isLoading && <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-405">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-406"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-407"></path>
        </svg>}
      {!isLoading && startContent && <span className="mr-2" data-id="element-408">{startContent}</span>}
      {children}
      {!isLoading && endContent && <span className="ml-2" data-id="element-409">{endContent}</span>}
    </button>;
}